// Modules
import moment from "moment";
import CountDownModal from "components/molecules/ModalContainer/CountDownModalContainer";
import { Form } from "antd";

// Hooks
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

// Redux
import { useAppDispatch, useAppSelector } from "app/hooks/hooks";
import { createRetroClaim, createInfoUpdater, retroCreateClaimDetailsSelector, getLoadingStateSelector } from "redux/retroClaim/retroClaimSlice";
import { getLoggedInUserInfo, getStoreList } from "redux/userList/userSlice";

// Constants & Utils
import { validateAmount, validateCardNumber, validateClaimReceiptNumber } from "utils/Validations/fieldValidations";
import { formatPaymentModeForApi } from "utils/paymentMode";
import { Routes, DateTimeFormats, REVIEW_RETRO_CLAIM_FORM } from "constants/global";

// Type Definitions
import type { DatePickerProps } from "antd";
import type { CreateClaimType, Users } from "redux/types";
import type { OutletItem } from "services/api/types";

export type ClaimDetails = {
	receipt_number: string;
	cr_card_number: string;
	purchase_amount: string;
	net_amount: string;
	source: string;
	payment_mode: string;
	receipt_datetime: string;
	receipt_imgurl: string;
};

export type ClaimFieldItem = {
	id: string;
	value: string;
};

export type ClaimFieldErrors = {
	receipt_number?: boolean;
	cr_card_number?: boolean;
	purchase_amount?: boolean;
	net_amount?: boolean;
};

const initialErrorState = {
	receipt_number: false,
	cr_card_number: false,
	purchase_amount: false,
	net_amount: false
};

export function useCreateRetrospectiveClaim() {
	const retroClaimDetails = useAppSelector(retroCreateClaimDetailsSelector);
	const userInfo: Users["user"] = useAppSelector(getLoggedInUserInfo);
	const dataLoading = useAppSelector(getLoadingStateSelector);
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const [form] = Form.useForm();

	const [claimDetails, setClaimDetails] = useState<ClaimDetails>({
		source: retroClaimDetails?.source ?? "",
		payment_mode: retroClaimDetails?.payment_mode ?? "",
		receipt_datetime: retroClaimDetails?.receipt_datetime ?? "",
		receipt_number: retroClaimDetails?.receipt_number ?? "",
		cr_card_number: retroClaimDetails?.cr_card_number ?? "",
		purchase_amount: retroClaimDetails?.purchase_amount?.toString() ?? "",
		net_amount: retroClaimDetails?.net_amount?.toString() ?? "",
		receipt_imgurl: retroClaimDetails?.receipt_img_base64 ?? ""
	});

	const [outletCode, setOutletCode] = useState<string>("");
	const [outletName, setOutletName] = useState<string>("");

	const [terminal, setTerminal] = useState<string>("");

	const [searchValue, setSearchValue] = useState<string>("");

	const [fieldErrors, setFieldErrors] = useState<ClaimFieldErrors>(initialErrorState);

	const [detailsFilled, setDetailsFilled] = useState<CreateClaimType>({});

	const [dateVal, setDateVal] = useState<string>("");

	const [storesDataList, setStoresDataList] = useState<Array<OutletItem>>([]);

	const getStoresInfo = useCallback(async () => {
		try {
			const { data } = await dispatch(getStoreList({})).unwrap();

			if (data) {
				setStoresDataList(data);
			}
		} catch (error) {
			const err = error as any;
			if (err?.response?.data?.errors && err?.response?.data?.errors?.length > 0) {
				CountDownModal({
					timer: 3,
					title: REVIEW_RETRO_CLAIM_FORM.CANNOT_SEND_REQUEST,
					errorIcon: true,
					onTimeComplete: goBack
				});
			}
		}
	}, [dispatch, getStoreList, setStoresDataList]);

	useEffect(() => {
		getStoresInfo();
	}, []);

	// Check all required fields are entered
	const isNextEnabled = useMemo(() => {
		const values = Object.values(claimDetails);
		const errors = Object.values(fieldErrors);

		for (let i = 0; i < values?.length; i++) {
			if (!values[i]) {
				return false;
			}
		}

		for (let i = 0; i < errors?.length; i++) {
			if (errors[i] === true) {
				return false;
			}
		}

		if (!outletCode?.length) {
			return false;
		}

		return true;
	}, [claimDetails, fieldErrors, retroClaimDetails, outletCode]);

	// Prepare payload once we have all the details
	// receipt_datetime needs to be converted to UTC before calling API
	useEffect(() => {
		const apiPayload = {
			receipt_number: claimDetails.receipt_number,
			outlet_code: outletCode,
			cr_card_number: claimDetails.cr_card_number,
			receipt_datetime: claimDetails.receipt_datetime,
			purchase_amount: parseFloat(claimDetails.purchase_amount),
			net_amount: parseFloat(claimDetails.net_amount),
			source: claimDetails.source,
			payment_mode: formatPaymentModeForApi(claimDetails.payment_mode),
			receipt_img_base64: claimDetails.receipt_imgurl
		};
		if (isNextEnabled) {
			setDetailsFilled(apiPayload);
		}
	}, [isNextEnabled, claimDetails, fieldErrors, retroClaimDetails]);

	// Handler for updating claim details entered
	const handleClaimDetailsChange = useCallback(
		({ id, value }: { id: string; value: string }) => {
			const newData = {
				...claimDetails,
				[id]: value
			};
			setClaimDetails(newData);
		},
		[claimDetails, setClaimDetails]
	);

	const handleInputChange = (e: any) => {
		handleClaimDetailsChange({
			id: e?.target?.id,
			value: e?.target?.value
		});
	};

	// Handler for Outlet Select
	const handleOutletSelect = (value: string) => {
		const optionValueArray = value.split("*");
		setOutletName(optionValueArray[0]);
		setOutletCode(optionValueArray[1]);
	};

	const handleOutletSearch = (value: string) => {
		setSearchValue(value);
	};

	const goBack = useCallback(() => {
		navigate(`${Routes.RETRO_CLAIM}`);
	}, [navigate]);

	// Handler for Receipt Date Time

	const handleOnDateChange = useCallback(
		(_value: DatePickerProps["value"], dateString: string) => {
			const receipt_dt = moment(dateString, [DateTimeFormats.USE_AM_PM]).format(DateTimeFormats.USE_AM_PM);
			setDateVal(receipt_dt);
		},
		[dateVal, setDateVal, moment]
	);

	const handleOnDateOk = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		(_value: DatePickerProps["value"]) => {
			const newData = {
				...claimDetails,
				receipt_datetime: moment(dateVal, [DateTimeFormats.USE_AM_PM]).format(DateTimeFormats.USE_SECONDS)
			};
			setClaimDetails(newData);
		},
		[claimDetails, dateVal, setClaimDetails]
	);

	// useEffect being used due to a possibility of ANT component onChange and onOk called at the same time
	useEffect(() => {
		if (dateVal !== "") {
			const newData = {
				...claimDetails,
				receipt_datetime: moment(dateVal, [DateTimeFormats.USE_AM_PM]).format(DateTimeFormats.USE_SECONDS)
			};
			setClaimDetails(newData);
		}
	}, [dateVal]);

	const handleSource = useCallback(
		(value: string) => {
			const newData = {
				...claimDetails,
				source: value
			};
			setClaimDetails(newData);
		},
		[claimDetails, setClaimDetails]
	);

	const handlePaymentMode = useCallback(
		(value: string) => {
			handleClaimDetailsChange({ id: "payment_mode", value });
		},
		[handleClaimDetailsChange]
	);

	// Handle Image Update
	const handleUpload = useCallback(
		(url: string) => {
			const newData = {
				...claimDetails,
				receipt_imgurl: url
			};
			setClaimDetails(newData);
		},
		[claimDetails, setClaimDetails]
	);

	const validateFields = useCallback(
		(submit: boolean) => {
			const fieldsToValidate = [
				{ key: "receipt_number", validator: validateClaimReceiptNumber },
				{ key: "cr_card_number", validator: validateCardNumber },
				{ key: "purchase_amount", validator: validateAmount },
				{ key: "net_amount", validator: validateAmount }
			];

			// reset error state before validations
			setFieldErrors(initialErrorState);

			const errors: Array<{ field: string | undefined }> = [];

			fieldsToValidate.forEach((field: { key: string; validator: (args: string) => boolean }) => {
				const key = field.key as keyof ClaimFieldErrors;
				const value = claimDetails[key];
				if (value || submit) {
					const isValid = field.validator(value);

					if (!isValid) {
						errors.push({ field: field.key });
					}
				}
			});

			if (errors.length) {
				const errs: ClaimFieldErrors = {};
				errors.forEach((err) => {
					errs[err.field as keyof ClaimFieldErrors] = true;
				});
				setFieldErrors(errs);
				return;
			}

			return true;
		},
		[claimDetails, fieldErrors, setFieldErrors]
	);

	const handleApprove = useCallback(async () => {
		dispatch(createInfoUpdater(detailsFilled));

		try {
			const { errors } = await dispatch(
				createRetroClaim({
					...detailsFilled,
					receipt_img_base64: claimDetails.receipt_imgurl.split(",")?.[1],
					receipt_datetime: moment(claimDetails.receipt_datetime).utc().format(DateTimeFormats.USE_SECONDS)
				})
			).unwrap();

			if (!errors?.length) {
				CountDownModal({
					timer: 3,
					title: "Claim successfully created!",
					onTimeComplete: () => navigate(`${Routes.RETRO_CLAIM}`)
				});
			}
		} catch (error) {
			const err = error as any;
			if (err?.response?.data?.errors && err?.response?.data?.errors?.length > 0) {
				CountDownModal({
					timer: 3,
					errorIcon: true,
					title: err?.response?.data?.errors?.[0]?.message,
					closable: true
				});
			}
		}
	}, [navigate, moment, detailsFilled, claimDetails, outletName]);

	const handleReject = useCallback(async () => {
		dispatch(createInfoUpdater(detailsFilled));

		try {
			const { errors, data } = await dispatch(
				createRetroClaim({
					...detailsFilled,
					receipt_img_base64: claimDetails.receipt_imgurl.split(",")?.[1],
					receipt_datetime: moment(claimDetails.receipt_datetime).utc().format(DateTimeFormats.USE_SECONDS)
				})
			).unwrap();

			if (!errors?.length) {
				navigate(`${Routes.RETRO_CLAIM}/${Routes.REJECT_RETRO_CLAIM}`, { state: { retroClaimId: data?.retro_claim_id, throughCreateClaim: true } });
			}
		} catch (error) {
			const err = error as any;
			if (err?.response?.data?.errors && err?.response?.data?.errors?.length > 0) {
				CountDownModal({
					timer: 3,
					errorIcon: true,
					title: err?.response?.data?.errors?.[0]?.message,
					closable: true
				});
			}
		}
	}, [navigate, moment, detailsFilled, claimDetails]);

	const { outletsList, terminalsList } = useMemo(() => {
		const terminalsListArr: Array<{ id: string; value: string }> = [];
		const outletsListArr: OutletItem[] = [];

		storesDataList?.forEach((store: OutletItem) => {
			if (store?.terminal && !terminalsListArr?.find(({ id }) => id === store?.terminal)) {
				terminalsListArr.push({ id: store?.terminal, value: store?.terminal });
			}
			/**
			 * Add outlets based on *terminal* selection. If no terminal do not show outlets.
			 */
			if (terminal && store?.outlet_code && !outletsListArr?.find(({ outlet_code }) => outlet_code === store?.outlet_code) && store?.terminal === terminal) {
				outletsListArr.push(store);
			}
		});
		return { outletsList: outletsListArr, terminalsList: terminalsListArr };
	}, [terminal, outletCode, outletName, setTerminal, setOutletCode, setOutletName, storesDataList]);

	const handleTerminalSelect = useCallback(
		(value: string) => {
			setTerminal(() => value);
			setOutletCode("");
			setOutletName("");
			form.setFieldsValue({ outlet_code: null });
		},
		[setTerminal, setOutletCode, setOutletName, form, terminal]
	);

	return {
		claimDetails,
		outletCode,
		fieldErrors,
		isNextEnabled,
		userInfo: userInfo?.data?.[0] ?? [],
		storesDataList,
		loading: dataLoading === 1,
		form,
		terminal,
		terminalsList,
		filteredOutletsList: outletsList,
		handleTerminalSelect,
		searchValue,
		validateFields,
		handleInputChange,
		handleOnDateChange,
		handleOnDateOk,
		handleSource,
		handlePaymentMode,
		handleUpload,
		handleApprove,
		handleReject,
		handleOutletSearch,
		handleOutletSelect,
		goBack
	};
}
